import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { NumberTextFieldNoWheelRHF } from './NumberTextFieldNoWheelRHF';
import BatchedTicketLinksPanel from './BatchedTicketLinksPanel';

const AUTO_ADVANCE_OPTIONS = [
  { value: 'date', label: 'Por data (padrão)' },
  { value: 'previous_sold_out', label: 'Quando lote anterior esgotar' },
  { value: 'previous_expired', label: 'Quando lote anterior expirar' },
  { value: 'manual', label: 'Manual (organizador controla)' },
];

interface BatchTimelineProps {
  typeIndex: number;
}

const BatchTimeline: React.FC<BatchTimelineProps> = ({ typeIndex }) => {
  const { control, watch } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `ticketTypes.${typeIndex}.batches`,
  });

  const addBatch = () => {
    append({
      name: `Lote ${fields.length + 1}`,
      price: watch(`ticketTypes.${typeIndex}.batches.0.price`) || 0,
      quantity: 100,
      startSaleDate: new Date(),
      endSaleDate: new Date(new Date().setDate(new Date().getDate() + 30)),
      sortOrder: fields.length,
      isActive: true,
      autoAdvanceWhen: 'date',
      linkOnly: false,
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>Lotes deste tipo</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addBatch}>Adicionar lote</Button>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {fields.map((field, batchIndex) => (
          <Paper key={field.id} variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Chip size="small" label={`Lote ${batchIndex + 1}`} />
              {fields.length > 1 && (
                <Tooltip title="Remover lote">
                  <IconButton size="small" color="error" onClick={() => remove(batchIndex)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Controller
                  name={`ticketTypes.${typeIndex}.batches.${batchIndex}.name`}
                  control={control}
                  render={({ field: f }) => (
                    <TextField {...f} fullWidth size="small" label="Nome do lote" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name={`ticketTypes.${typeIndex}.batches.${batchIndex}.price`}
                  control={control}
                  render={({ field: f }) => (
                    <NumberTextFieldNoWheelRHF
                      field={f}
                      fullWidth
                      size="small"
                      type="number"
                      label="Preço (R$)"
                      InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name={`ticketTypes.${typeIndex}.batches.${batchIndex}.quantity`}
                  control={control}
                  render={({ field: f }) => (
                    <NumberTextFieldNoWheelRHF field={f} fullWidth size="small" type="number" label="Vagas" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                  <Controller
                    name={`ticketTypes.${typeIndex}.batches.${batchIndex}.startSaleDate`}
                    control={control}
                    render={({ field: f }) => (
                      <DateTimePicker
                        label="Início"
                        value={f.value ?? null}
                        onChange={(d) => f.onChange(d)}
                        ampm={false}
                        format="dd/MM/yyyy HH:mm"
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                  <Controller
                    name={`ticketTypes.${typeIndex}.batches.${batchIndex}.endSaleDate`}
                    control={control}
                    render={({ field: f }) => (
                      <DateTimePicker
                        label="Fim"
                        value={f.value ?? null}
                        onChange={(d) => f.onChange(d)}
                        ampm={false}
                        format="dd/MM/yyyy HH:mm"
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name={`ticketTypes.${typeIndex}.batches.${batchIndex}.autoAdvanceWhen`}
                  control={control}
                  render={({ field: f }) => (
                    <TextField {...f} select fullWidth size="small" label="Abrir próximo lote quando">
                      {AUTO_ADVANCE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name={`ticketTypes.${typeIndex}.batches.${batchIndex}.linkOnly`}
                  control={control}
                  render={({ field: f }) => (
                    <FormControlLabel
                      control={<Switch checked={!!f.value} onChange={(e) => f.onChange(e.target.checked)} />}
                      label="Somente por link (desconto exclusivo)"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

const BatchedTicketsForm: React.FC = () => {
  const { control, watch } = useFormContext();
  const slug = watch('slug') || '';
  const { fields, append, remove } = useFieldArray({ control, name: 'ticketTypes' });
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  const [mainTab, setMainTab] = useState<'tickets' | 'links'>('tickets');

  const addType = () => {
    const idx = fields.length;
    append({
      name: `Ingresso ${idx + 1}`,
      description: '',
      sortOrder: idx,
      isActive: true,
      hiddenFromPublicPage: false,
      isRestricted: false,
      allowedEmails: [],
      showPlatformFee: false,
      batches: [{
        name: 'Lote 1',
        price: 0,
        quantity: 100,
        startSaleDate: new Date(),
        endSaleDate: new Date(new Date().setDate(new Date().getDate() + 30)),
        sortOrder: 0,
        isActive: true,
        autoAdvanceWhen: 'date',
        linkOnly: false,
      }],
    });
    setExpandedIndex(idx);
  };

  const hiddenCount = fields.filter((_, i) => watch(`ticketTypes.${i}.hiddenFromPublicPage`)).length;
  const publicCount = fields.length - hiddenCount;
  const publicTypeEntries = fields
    .map((field, typeIndex) => ({ field, typeIndex }))
    .filter(({ typeIndex }) => !watch(`ticketTypes.${typeIndex}.hiddenFromPublicPage`));

  return (
    <Box>
      <Paper elevation={0} variant="outlined">
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            value="tickets"
            icon={<ConfirmationNumberOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label={`Ingressos (${publicCount})`}
          />
          <Tab
            value="links"
            icon={<LinkOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label={`Links promocionais${hiddenCount ? ` (${hiddenCount})` : ''}`}
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {mainTab === 'tickets' && (
            <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cadastre cada ingresso (Inteira, Meia…). Se vender em etapas (Lote 1, Lote 2), configure os lotes dentro do ingresso.
      </Typography>

      {hiddenCount > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Links com desconto ficam na aba &quot;Links promocionais&quot; — não aparecem aqui como ingresso separado.
        </Typography>
      )}

      {publicTypeEntries.map(({ field, typeIndex }) => {
        const typeName = watch(`ticketTypes.${typeIndex}.name`) || `Tipo ${typeIndex + 1}`;
        const batchCount = watch(`ticketTypes.${typeIndex}.batches`)?.length || 0;
        return (
          <Accordion
            key={field.id}
            expanded={expandedIndex === typeIndex}
            onChange={(_, exp) => setExpandedIndex(exp ? typeIndex : null)}
            disableGutters
            sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', pr: 1 }}>
                <Typography fontWeight={600}>{typeName}</Typography>
                <Chip size="small" label={`${batchCount} lote(s)`} />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`ticketTypes.${typeIndex}.name`}
                    control={control}
                    render={({ field: f }) => <TextField {...f} fullWidth label="Nome do ingresso *" />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`ticketTypes.${typeIndex}.description`}
                    control={control}
                    render={({ field: f }) => <TextField {...f} fullWidth label="Descrição" />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`ticketTypes.${typeIndex}.hiddenFromPublicPage`}
                    control={control}
                    render={({ field: f }) => (
                      <FormControlLabel
                        control={<Switch checked={!!f.value} onChange={(e) => f.onChange(e.target.checked)} />}
                        label="Ocultar na página (só quem tiver o link)"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`ticketTypes.${typeIndex}.isRestricted`}
                    control={control}
                    render={({ field: f }) => (
                      <FormControlLabel
                        control={<Switch checked={!!f.value} onChange={(e) => f.onChange(e.target.checked)} />}
                        label="Lista de convidados restrita"
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <BatchTimeline typeIndex={typeIndex} />
              {publicCount > 1 && (
                <Button color="error" size="small" sx={{ mt: 2 }} onClick={() => remove(typeIndex)}>
                  Remover tipo
                </Button>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Button variant="outlined" startIcon={<AddIcon />} onClick={addType} sx={{ mt: 1 }}>
        Adicionar ingresso
      </Button>
            </>
          )}

          {mainTab === 'links' && <BatchedTicketLinksPanel slug={slug} />}
        </Box>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Dica: use a aba &quot;Links promocionais&quot; para criar desconto em % — o sistema calcula o preço automaticamente.
      </Typography>
    </Box>
  );
};

export default BatchedTicketsForm;
